/**
 * KioskKeypad - Custom on-screen keyboard for portrait kiosk mode
 * Prevents native keyboard from appearing (which would be in wrong orientation)
 */
import { Delete } from 'lucide-react';

// Numeric keypad layout
const NUMERIC_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'backspace'];

// Full QWERTY keyboard layout
const QWERTY_ROWS = [
  ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
  ['z', 'x', 'c', 'v', 'b', 'n', 'm', 'backspace'],
  ['@', '.', 'space', '.com', 'done']
];

export function NumericKeypad({ onKeyPress, onDone, doneText = 'Done', isAccessible = false, disabled = false }) {
  const handlePress = (key) => {
    if (key === 'done') {
      onDone?.();
    } else {
      onKeyPress?.(key);
    }
  };

  return (
    <div className="bg-gray-900 rounded-2xl p-4 border border-gray-700">
      <div className="grid grid-cols-3 gap-3">
        {NUMERIC_KEYS.map((key) => (
          <button
            key={key}
            onClick={() => handlePress(key)}
            className={`${isAccessible ? 'h-20 text-3xl' : 'h-16 text-2xl'} rounded-xl font-semibold transition-all active:scale-95 ${
              key === 'clear'
                ? 'bg-red-600 hover:bg-red-700 text-white text-base'
                : key === 'backspace'
                  ? 'bg-gray-700 hover:bg-gray-600 text-white'
                  : 'bg-gray-800 hover:bg-gray-700 text-white'
            }`}
            data-testid={`keypad-${key}`}
          >
            {key === 'clear' ? 'Clear' : key === 'backspace' ? <Delete className="w-6 h-6 mx-auto" /> : key}
          </button>
        ))}
      </div>
      
      {/* Done Button */}
      <button
        onClick={() => handlePress('done')}
        disabled={disabled}
        className={`w-full mt-3 ${isAccessible ? 'py-5 text-2xl' : 'py-4 text-xl'} bg-primary hover:bg-primary/90 disabled:bg-gray-700 disabled:text-gray-500 text-white font-semibold rounded-xl transition-colors`}
        data-testid="keypad-done"
      >
        {doneText}
      </button>
    </div>
  );
}

export function AlphanumericKeypad({ onKeyPress, onDone, doneText = 'Done', isAccessible = false, disabled = false }) {
  const handlePress = (key) => {
    if (key === 'done') {
      onDone?.();
    } else if (key === 'space') {
      onKeyPress?.(' ');
    } else {
      onKeyPress?.(key);
    }
  };

  return (
    <div className="bg-gray-900 rounded-2xl p-3 border border-gray-700">
      {QWERTY_ROWS.map((row, rowIndex) => (
        <div key={rowIndex} className="flex justify-center gap-1.5 mb-1.5">
          {row.map((key) => {
            const isSpecial = ['backspace', 'space', '.com', 'done', 'clear'].includes(key);
            const isWide = key === 'space';
            const isDone = key === 'done';
            
            return (
              <button
                key={key}
                onClick={() => handlePress(key)}
                disabled={isDone && disabled}
                className={`${isAccessible ? 'h-14 text-xl' : 'h-12 text-lg'} ${
                  isWide ? 'flex-1 min-w-[120px]' : isDone ? 'px-6' : 'w-10'
                } rounded-lg font-medium transition-all active:scale-95 ${
                  isDone
                    ? 'bg-primary hover:bg-primary/90 disabled:bg-gray-700 disabled:text-gray-500 text-white'
                    : key === 'backspace'
                      ? 'bg-gray-700 hover:bg-gray-600 text-white w-16'
                      : key === '.com'
                        ? 'bg-gray-700 hover:bg-gray-600 text-white text-sm w-14'
                        : 'bg-gray-800 hover:bg-gray-700 text-white'
                }`}
                data-testid={`keypad-${key}`}
              >
                {key === 'backspace' ? (
                  <Delete className="w-5 h-5 mx-auto" />
                ) : key === 'space' ? (
                  'space'
                ) : key === 'done' ? (
                  doneText
                ) : (
                  key
                )}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default NumericKeypad;
